import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, signal, untracked, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { ContentStateService } from '../../../content-state.service';
import { Chunk } from '../../chunk.dto';
import { ChunkService } from '../../chunk.service';

@Component({
  selector: 'app-transcript-chunk-displayer',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  styleUrls: ['./transcript-chunk-displayer.scss'],
  template: `
    @if (chunk(); as chunk) {
      <div class="transcript-card" [class.is-expanded]="isExpanded()">
        <!-- Header -->
        <div class="transcript-card-header">
          @if (chunk.status === 'processing') {
            <div class="transcript-loader-box">
              <div class="skz-loader-small"></div>
            </div>
          } @else if (chunk.status === 'failed') {
            <div class="transcript-icon-box error" matTooltipPosition="above">
              <mat-icon class="transcript-icon" color="warn">error</mat-icon>
            </div>
          } @else {
            <div class="transcript-icon-box" matTooltipPosition="above">
              <mat-icon class="transcript-icon">wysiwyg</mat-icon>
            </div>
          }

          @if (isEditingTitle()) {
            <div class="edit-title-container" (click)="$event.stopPropagation()">
              <input #titleInput type="text" [value]="tempTitle()" (keydown.enter)="saveTitle(titleInput.value)" (keydown.escape)="cancelEdit()" class="title-edit-input" />
              <button mat-icon-button class="edit-action-btn confirm-btn" (click)="saveTitle(titleInput.value)">
                <mat-icon>check</mat-icon>
              </button>
              <button mat-icon-button class="edit-action-btn cancel-btn" (click)="cancelEdit()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          } @else {
            <span class="transcript-card-title" [class.editable]="isWriter()" (click)="isWriter() && startEdit($event)">
              {{ formattedTitle() }}
            </span>
          }

          <div class="transcript-card-actions">
            @if (chunk.status !== 'processing') {
              <button mat-icon-button class="copy-btn" (click)="$event.stopPropagation(); copyText(chunk)" [matTooltip]="'Global.Copy' | translate">
                <mat-icon class="copy-icon">content_copy</mat-icon>
              </button>
              <button mat-icon-button class="open-btn" (click)="$event.stopPropagation(); viewTranscript()" [matTooltip]="(isWriter() ? 'Global.Edit' : 'Global.Open') | translate">
                <mat-icon class="open-icon">expand_content</mat-icon>
              </button>
            }
          </div>
        </div>

        <!-- Transcript Preview -->
        <div class="transcript-preview-area">
          @if (chunk.status === 'processing') {
            <div class="transcript-status-container processing">
              <span>{{ 'Content.Chunk.TranscriptionInProgress' | translate }}</span>
            </div>
          } @else if (chunk.status === 'failed') {
            <div class="transcript-status-container failed">
              <span>{{ 'Content.Chunk.TranscriptionFailed' | translate }}</span>
            </div>
          } @else if (chunk.mainEdition?.content) {
            <div class="transcript-preview-container custom-scrollbar" [class.expandable]="isExpandable()" [class.expanded]="isExpanded()" [innerHTML]="sanitizedHTML()"></div>
          }

          <!-- Expansion Overlay -->
          @if (isExpandable() && chunk.status !== 'processing' && chunk.status !== 'failed') {
            <div class="expansion-overlay">
              <div class="show-more-pill" (click)="$event.stopPropagation(); toggleExpand()">
                <span>{{ (isExpanded() ? 'Global.ShowLess' : 'Global.ShowMore') | translate }}</span>
                <mat-icon class="show-more-icon" [class.rotated]="isExpanded()">expand_more</mat-icon>
              </div>
            </div>
          }
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranscriptChunkDisplayerComponent {
  chunk = input.required<Chunk>();

  private contentStateService = inject(ContentStateService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private sanitizer = inject(DomSanitizer);

  private chunkService = inject(ChunkService);
  isEditingTitle = signal(false);
  tempTitle = signal('');

  @ViewChild('titleInput') set titleInput(element: ElementRef<HTMLInputElement>) {
    if (element) {
      const input = element.nativeElement;
      input.focus();
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  }

  isWriter = computed(() => this.authService.isWriter() && !this.contentStateService.isReader());
  isExpanded = signal(false);

  isPlaying = computed(() => {
    const playerState = this.contentStateService.playerState();
    return playerState.chunkId === this.chunk()?._id && playerState.status === 'playing';
  });

  constructor() {
    effect(() => {
      if (this.isPlaying()) {
        untracked(() => {
          this.isExpanded.set(true);
        });
      }
    });
  }

  formattedTitle = computed(() => {
    const chunkData = this.chunk();
    if (!chunkData) return '';
    if (chunkData.title) return chunkData.title;

    let title = this.translate.instant('Global.Transcript');
    if (chunkData.createdAt) {
      const datePipe = new DatePipe(this.translate.currentLang);
      title += ' - ' + datePipe.transform(chunkData.createdAt, 'mediumDate') + ', ' + datePipe.transform(chunkData.createdAt, 'shortTime');
    }
    return title;
  });

  isExpandable = computed(() => {
    const content = this.chunk()?.mainEdition?.content || '';
    // Determine if text is long enough to warrant collapse/expansion
    return content.length > 300 || content.split('<p').length > 3 || content.split('\n').length > 5;
  });

  sanitizedHTML = computed<SafeHtml>(() => {
    const content = this.chunk()?.mainEdition?.content || '';
    return this.sanitizer.bypassSecurityTrustHtml(content);
  });

  toggleExpand(): void {
    this.isExpanded.update((v) => !v);
  }

  startEdit(event: MouseEvent): void {
    event.stopPropagation();
    this.tempTitle.set(this.chunk().title || this.translate.instant('Global.Transcript'));
    this.isEditingTitle.set(true);
  }

  cancelEdit(): void {
    this.isEditingTitle.set(false);
  }

  saveTitle(newTitle: string): void {
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      this.isEditingTitle.set(false);
      return;
    }

    const chunkData = this.chunk();
    if (trimmedTitle === (chunkData.title || this.translate.instant('Global.Transcript'))) {
      this.isEditingTitle.set(false);
      return;
    }

    const edition = chunkData.mainEdition || chunkData.editions?.[0];
    if (!edition) {
      this.isEditingTitle.set(false);
      return;
    }

    this.chunkService
      .editChunk({
        id: chunkData._id,
        edition: edition,
        content: edition.content || '',
        contentID: chunkData.contentID || this.contentStateService.contentId,
        language: this.translate.currentLang,
        title: trimmedTitle,
      })
      .subscribe({
        next: () => {
          this.isEditingTitle.set(false);
        },
        error: (err) => {
          console.error('Failed to update chunk title', err);
          this.isEditingTitle.set(false);
        },
      });
  }

  viewTranscript(): void {
    const chunkData = this.chunk();
    const currentTitle = this.formattedTitle();
    this.contentStateService.openCanvas({
      title: currentTitle,
      html: chunkData.mainEdition?.content || '',
      source: 'transcript',
      chunkId: chunkData._id,
      contentId: chunkData.contentID || this.contentStateService.contentId,
      edition: chunkData.mainEdition,
    });

    this.contentStateService.openContentGenerator({
      initialSubject: currentTitle,
      context: {
        id: chunkData._id,
        title: currentTitle,
        name: currentTitle,
        type: 'chunk',
      },
      isAuto: true,
    });
  }

  copyText(chunk: Chunk): void {
    const content = chunk.mainEdition?.content;
    if (content) {
      // Convert HTML elements into plain text for user-friendly clipboard copy
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      navigator.clipboard.writeText(plainText);
    }
  }
}
